export const DEFAULT_TASK_GEO_RADIUS_METERS = 200;

export const TASK_GEO_POSITION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 0,
};

function toFiniteNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function calculateGeoDistanceMeters(lat1, lng1, lat2, lng2) {
  const firstLat = toFiniteNumber(lat1);
  const firstLng = toFiniteNumber(lng1);
  const secondLat = toFiniteNumber(lat2);
  const secondLng = toFiniteNumber(lng2);

  if ([firstLat, firstLng, secondLat, secondLng].some((value) => value == null)) {
    return null;
  }

  const earthRadiusMeters = 6371000;
  const toRadians = (degrees) => (degrees * Math.PI) / 180;
  const deltaLat = toRadians(secondLat - firstLat);
  const deltaLng = toRadians(secondLng - firstLng);
  const startLat = toRadians(firstLat);
  const endLat = toRadians(secondLat);

  const haversine =
    Math.sin(deltaLat / 2) ** 2
    + Math.cos(startLat) * Math.cos(endLat) * Math.sin(deltaLng / 2) ** 2;

  return 2 * earthRadiusMeters * Math.asin(Math.sqrt(haversine));
}

export function formatGeoDistance(distanceMeters) {
  const distance = toFiniteNumber(distanceMeters);
  if (distance == null) {
    return "--";
  }

  if (Math.abs(distance) >= 1000) {
    return `${(distance / 1000).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} km`;
  }

  return `${Math.round(distance).toLocaleString("pt-BR")} m`;
}

export function formatGeoAccuracy(accuracyMeters) {
  const accuracy = toFiniteNumber(accuracyMeters);
  if (accuracy == null) {
    return "--";
  }
  return `${Math.round(accuracy).toLocaleString("pt-BR")} m`;
}

export function formatGeoCoordinate(value) {
  const coordinate = toFiniteNumber(value);
  if (coordinate == null) {
    return "--";
  }
  return coordinate.toLocaleString("pt-BR", {
    minimumFractionDigits: 6,
    maximumFractionDigits: 6,
  });
}

export function resolveGeoBrowserError(error) {
  if (!error || typeof error !== "object") {
    return "Nao foi possivel obter localizacao";
  }

  switch (error.code) {
    case 1:
      return "Permissao de localizacao negada";
    case 2:
      return "Localizacao indisponivel no dispositivo";
    case 3:
      return "Tempo limite excedido ao obter localizacao";
    default:
      return error.message || "Nao foi possivel obter localizacao";
  }
}

export function parseGeoErrorInfo(message) {
  const text = String(message || "");
  const distMatch = text.match(/Distancia atual:?\s*([0-9.,]+)\s*m/i);
  const raioMatch = text.match(/Raio maximo:?\s*([0-9.,]+)\s*m/i);
  return {
    distancia: distMatch?.[1] || null,
    raio: raioMatch?.[1] || null,
  };
}

export function buildPartnerAddressLabel(partner) {
  if (!partner) {
    return "";
  }

  const cityUf = [partner.cidade, partner.uf]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(" - ");

  return [partner.endereco, partner.nu_endereco, partner.bairro, cityUf, partner.cep]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
}

export function buildTaskGeoPartner({ task, partner = null, partnerLookup = null } = {}) {
  if (!task && !partner) {
    return null;
  }

  const partnerIds = Array.isArray(task?.parceirosIds) ? task.parceirosIds : [];
  const partnerNames = Array.isArray(task?.parceirosNomes) ? task.parceirosNomes : [];
  const primaryPartnerId = partner?.id ?? partnerIds[0] ?? null;
  const lookedUpPartner =
    partner
    || (partnerLookup && primaryPartnerId != null ? partnerLookup.get(String(primaryPartnerId)) : null)
    || null;

  return {
    id: lookedUpPartner?.id ?? primaryPartnerId ?? null,
    nome: lookedUpPartner?.nome || partnerNames[0] || "Parceiro vinculado",
    latitude: toFiniteNumber(lookedUpPartner?.latitude),
    longitude: toFiniteNumber(lookedUpPartner?.longitude),
    addressLabel: buildPartnerAddressLabel(lookedUpPartner),
  };
}
